import { memo, MouseEvent, MouseEventHandler } from 'react'
import { Home as HomeIcon, School as SchoolIcon } from '@mui/icons-material'
import { Box, MenuItem } from '@mui/material'

import { Institution } from 'domain/entities/representative'
import { Notification } from 'domain/entities/notifications'
import { TextParagraph } from 'presentation/components/elements'
import {
  HeaderAccountUserNavItem,
  HeaderMenu,
  HeaderNotificationsNavItem,
  HeaderWrapperNavBarItems,
} from 'presentation/components/layout/components/Header/components'
import { StyledItemMenuText } from 'presentation/components/layout/components/Header/header.styled'
import { mainMenuText, schoolText, selectChildText } from 'presentation/constant/texts/header'
import EllipseIcon from 'presentation/assets/icons/ellipse.svg'
import { NavItemPopup } from './components'

type HeaderNavItemsProps = {
  selectedItemMenu: Institution
  isOpenNotificationsMenu: boolean
  isOpenUserMenu: boolean
  isOpenStudentMenu: boolean
  isOpenBackdrop: boolean
  isLoading: boolean
  anchorNotificationsEl: HTMLElement | null
  anchorStudentEl: HTMLElement | null
  anchorUserEl: HTMLElement | null
  errorGetInstitutionsByRepresentative: string
  error: string
  idsArray: { ids: string[] }
  institutions: Institution[]
  notifications: Notification[]
  handleCloseNotificationsMenu: () => void
  handleCloseUserMenu: () => void
  handleCloseStudentMenu: () => void
  handleClickMainMenu: () => void
  handleOpenBackdrop: () => void
  handleCloseBackdrop: () => void
  handleGetNotifications: () => void
  handleClickItemMenu: (event: MouseEvent<HTMLElement>, item: Institution) => void
  handleClickListNotificationsItemMenu: MouseEventHandler
  handleClickListStudentItemMenu: MouseEventHandler
  handleClickListUserItemMenu: MouseEventHandler
}

function HeaderNavItems({
  isOpenNotificationsMenu,
  isOpenUserMenu,
  anchorStudentEl,
  isOpenStudentMenu,
  handleCloseUserMenu,
  handleCloseStudentMenu,
  handleCloseNotificationsMenu,
  anchorNotificationsEl,
  handleClickListNotificationsItemMenu,
  selectedItemMenu,
  institutions,
  handleClickMainMenu,
  errorGetInstitutionsByRepresentative,
  handleClickListStudentItemMenu,
  handleClickItemMenu,
  error,
  notifications,
  handleOpenBackdrop,
  handleClickListUserItemMenu,
  anchorUserEl,
  isOpenBackdrop,
  isLoading,
  handleCloseBackdrop,
  idsArray,
  handleGetNotifications,
}: HeaderNavItemsProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        columnGap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <HeaderWrapperNavBarItems
        icon={<HomeIcon color="info" fontSize="large" />}
        isShowArrowDownIcon={false}
        handleOpenMenu={handleClickMainMenu}
      >
        <StyledItemMenuText fontWeight={300} sx={{ lineHeight: '30px' }}>
          {mainMenuText}
        </StyledItemMenuText>
      </HeaderWrapperNavBarItems>
      <HeaderWrapperNavBarItems
        icon={<SchoolIcon color="info" fontSize="large" />}
        handleOpenMenu={handleClickListStudentItemMenu}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
          }}
        >
          {selectedItemMenu ? (
            <>
              <StyledItemMenuText fontWeight={300} sx={{ lineHeight: '18px' }}>
                {schoolText}
              </StyledItemMenuText>
              <StyledItemMenuText fontWeight={500} sx={{ lineHeight: '18px' }}>
                {selectedItemMenu?.name}
              </StyledItemMenuText>
            </>
          ) : (
            <StyledItemMenuText fontWeight={300} sx={{ lineHeight: '30px' }}>
              {selectChildText}
            </StyledItemMenuText>
          )}
        </Box>
        <HeaderMenu
          anchorEl={anchorStudentEl}
          isOpenMenu={isOpenStudentMenu}
          handleCloseMenu={handleCloseStudentMenu}
        >
          {errorGetInstitutionsByRepresentative && (
            <TextParagraph>{errorGetInstitutionsByRepresentative}</TextParagraph>
          )}
          {institutions.length ? (
            institutions.map((institution: Institution) => (
              <MenuItem
                key={institution?.institutionId}
                onClick={(event) => handleClickItemMenu(event, institution)}
                selected={institution?.institutionId === selectedItemMenu?.institutionId}
              >
                {selectedItemMenu?.institutionId === institution?.institutionId ? (
                  <img
                    alt={EllipseIcon}
                    width={6}
                    src={EllipseIcon}
                    style={{ marginRight: '10px' }}
                  />
                ) : (
                  ''
                )}
                <StyledItemMenuText fontWeight={300} sx={{ lineHeight: '30px' }}>
                  {institution?.name}
                </StyledItemMenuText>
              </MenuItem>
            ))
          ) : (
            <Box sx={{ padding: '10px' }}>
              <StyledItemMenuText fontWeight={300} sx={{ lineHeight: '30px' }}>
                Aun no tiene hijos inscritos en ninguna prooción
              </StyledItemMenuText>
            </Box>
          )}
        </HeaderMenu>
      </HeaderWrapperNavBarItems>
      <HeaderNotificationsNavItem
        handleClickListNotificationsItemMenu={handleClickListNotificationsItemMenu}
        handleCloseNotificationsMenu={handleCloseNotificationsMenu}
        isOpenNotificationsMenu={isOpenNotificationsMenu}
        anchorNotificationsEl={anchorNotificationsEl}
        handleOpenBackdrop={handleOpenBackdrop}
        notifications={notifications}
        error={error as string}
      />
      <HeaderAccountUserNavItem
        isOpenUserMenu={isOpenUserMenu}
        handleClickListUserItemMenu={handleClickListUserItemMenu}
        handleCloseUserMenu={handleCloseUserMenu}
        anchorUserEl={anchorUserEl}
      />
      {isOpenBackdrop && (
        <NavItemPopup
          openBackdrop={isOpenBackdrop}
          handleCloseBackdrop={handleCloseBackdrop}
          idsArray={idsArray}
          handleCloseNotificationsMenu={handleCloseNotificationsMenu}
          handleNotifications={handleGetNotifications}
          isLoading={isLoading}
        />
      )}
    </Box>
  )
}

export default memo(HeaderNavItems)
